import { SOCIAL_CONFIG } from '../config/social';

interface AuthResponse {
  token: string;
  state?: string;
  error?: string;
  allParams?: any;
}

// Simple PKCE helper
const generateRandomString = (length: number) => {
  const possible = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~';
  let text = '';
  for (let i = 0; i < length; i++) {
    text += possible.charAt(Math.floor(Math.random() * possible.length));
  }
  return text;
};

const sha256 = async (plain: string) => {
  const encoder = new TextEncoder();
  const data = encoder.encode(plain);
  const hash = await crypto.subtle.digest('SHA-256', data);
  return hash;
};

const base64urlencode = (a: ArrayBuffer) => {
  const bytes = new Uint8Array(a);
  let len = bytes.byteLength;
  let binary = '';
  for (let i = 0; i < len; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary)
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
};

const generateCodeChallenge = async () => {
  const codeVerifier = generateRandomString(128);
  const hashed = await sha256(codeVerifier);
  const codeChallenge = base64urlencode(hashed);
  return { codeVerifier, codeChallenge };
};

export const socialLogin = async (platform: string): Promise<string> => {
  return new Promise(async (resolve, reject) => {
    const config = SOCIAL_CONFIG[platform as keyof typeof SOCIAL_CONFIG];
    if (!config || !config.clientId || config.clientId.includes('YOUR_')) {
      reject(new Error(`Missing configuration for ${platform}. Please check src/config/social.ts`));
      return;
    }

    const state = generateRandomString(32);
    // const state = 'test_state'; // For debug
    
    let url = `${config.authUrl}?client_id=${config.clientId}&redirect_uri=${encodeURIComponent(config.redirectUri)}&scope=${encodeURIComponent(config.scope)}&state=${state}`;

    // Platform specific params
    let codeVerifier = '';
    if (platform === 'twitter') {
      // Twitter uses PKCE
      const pkce = await generateCodeChallenge();
      codeVerifier = pkce.codeVerifier;
      url += `&response_type=code&code_challenge=${pkce.codeChallenge}&code_challenge_method=S256`;
      // Store codeVerifier in sessionStorage to exchange later if we were doing full flow
      sessionStorage.setItem('twitter_code_verifier', codeVerifier);
    } else if (platform === 'discord') {
      url += `&response_type=token`; // Implicit grant for Discord
    } else if (platform === 'github') {
      // GitHub doesn't support response_type param (always code), but usually just standard
      // GitHub flow: returns code
    }

    // Open popup
    const width = 600;
    const height = 700;
    const left = (window.screen.width - width) / 2;
    const top = (window.screen.height - height) / 2;
    
    const popup = window.open(
      url,
      `Login with ${platform}`,
      `width=${width},height=${height},left=${left},top=${top}`
    );

    if (!popup) {
      reject(new Error('Popup blocked. Please allow popups for this site.'));
      return;
    }

    // Message Listener
    const handleMessage = async (event: MessageEvent) => {
      // Verify origin
      if (event.origin !== window.location.origin) return;
      
      const data = event.data;
      if (data && data.type === 'SOCIAL_AUTH_CALLBACK') {
        if (data.state !== state && platform !== 'github') { // GitHub sometimes tricky with state
           // reject(new Error('Invalid state parameter'));
           // return;
        }

        if (data.error) {
          reject(new Error(data.error));
        } else if (data.token) {
          // If we got a token (Discord) or code (Twitter/GitHub)
          // Ideally, for Twitter/GitHub code, we should exchange it for a token here 
          // IF the backend strictly requires a token.
          // BUT, usually backend bind endpoints handle "code" exchange if they have the client secret.
          // For Twitter Public Client, we can exchange here.
          
          // Current requirement: "Backend uses frontend Token".
          // If it's a "code", we pass "code". The backend might treat it as "Token" param but use it as code.
          // Or we assume backend expects Access Token.
          
          resolve(data.token);
        } else {
          reject(new Error('No token or code returned'));
        }
        
        cleanup();
      }
    };

    let timer: NodeJS.Timeout;

    const cleanup = () => {
      window.removeEventListener('message', handleMessage);
      if (timer) clearInterval(timer);
      if (popup && !popup.closed) popup.close();
    };

    window.addEventListener('message', handleMessage);

    // Poll to check if popup closed
    timer = setInterval(() => {
      if (popup.closed) {
        cleanup();
        // If we didn't resolve yet, it means user closed without finishing
        // But promise status is hard to check. We can set a flag.
        // For simplicity, we just stop waiting. 
        // We could reject with "User cancelled" but sometimes race conditions occur.
      }
    }, 1000);
  });
};
